'use client';

import type { ButtonProps } from '@magicschool/spellbook/components/Button';
import { Button } from '@magicschool/spellbook/components/Button';
import type { FilterChipSelectProps } from '@magicschool/spellbook/components/FilterChipSelect';
import { useFilterChipSelect } from '@magicschool/spellbook/components/FilterChipSelect/FilterChipSelectProvider';
import type { IconProps } from '@magicschool/spellbook/components/Icon';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { NotificationBadge } from '@magicschool/spellbook/components/NotificationBadge';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import type { TooltipProps } from '@magicschool/spellbook/components/Tooltip';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { getControlIconSize } from '@magicschool/spellbook/lib/controls';
import { popperContentMotionClassNames, popperContentSideOffsetClassNames } from '@magicschool/spellbook/lib/popperContent';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import * as DropdownMenuPrimitive from 'radix-ui/dropdown-menu';
import { useMemo, useRef } from 'react';

export const FilterChipSelectTriggerSlot = Slot<
  React.ComponentProps<typeof DropdownMenuPrimitive.Trigger> &
    ButtonProps & {
      value?: string | string[];
      defaultValue?: string | string[];
      isOpen?: boolean;
      multiple?: boolean;
    }
>({
  displayName: 'FilterChipSelect.Trigger',
  preventOverride: true,
  renderDefault: ({ children, className, disabled, size = 'md', asChild = true, ...props }) => {
    const { options, value, maxItemsToShow, multiple } = useFilterChipSelect();

    const { slots, rest } = useSlots(children, {
      leadingIcon: { Component: FilterChipSelectTriggerLeadingIconSlot, props },
    });

    const selectedItems = useMemo(() => {
      if (!value || !options) return [];
      if (!multiple && typeof value === 'string') {
        const item = options.get(value) || { value, label: value };
        return [item];
      }

      if (Array.isArray(value)) {
        return value.map((val) => options.get(val) || { value: val, label: val });
      }

      return [];
    }, [value, options, multiple]);

    const displayItems = useMemo(() => {
      if (!selectedItems.length) return [];
      if (!multiple) return selectedItems;
      return maxItemsToShow !== undefined && maxItemsToShow >= 0 ? selectedItems.slice(0, maxItemsToShow) : selectedItems;
    }, [selectedItems, maxItemsToShow, multiple]);

    const displayText = useMemo(() => {
      if (!displayItems.length) return rest || 'Select items';
      return displayItems.map((item) => item.label || item.value).join(', ');
    }, [displayItems, rest]);

    const overflowCount = useMemo(() => {
      if (!multiple) return 0;
      if (maxItemsToShow === undefined || maxItemsToShow < 0) return 0;
      return Math.max(0, selectedItems.length - maxItemsToShow);
    }, [selectedItems.length, maxItemsToShow, multiple]);

    const notificationBadgeSize = useMemo(() => {
      if (size === 'sm') return 'xs';
      return 'sm';
    }, [size]);

    return (
      <DropdownMenuPrimitive.Trigger asChild={asChild} disabled={disabled} {...props}>
        <Button
          color="neutral"
          variant="outlined"
          size={size}
          disabled={disabled}
          data-spellbook-name="filter-chip-select-trigger"
          aria-pressed={selectedItems.length > 0}
          className={cn(
            [
              /**
               * @TODO [SPE-16] Centralize "Selected" Color Tokens into Shared Style Utilities
               * https://linear.app/magicschool/issue/SPE-16/centralize-selected-color-tokens-into-shared-style-utilities
               */
              'aria-pressed:[&[aria-disabled="true"]:not([aria-busy="true"])]:border-disabled-background',
              'aria-pressed:[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-disabled-background',
              'aria-pressed:[&[aria-disabled="true"]:not([aria-busy="true"])]:text-disabled-foreground',
              'aria-pressed:bg-selected-default-background',
              'aria-pressed:hover:bg-selected-default-background-hover',
              'aria-pressed:active:bg-selected-default-background-active',
              'aria-pressed:text-selected-default-foreground',
              'aria-pressed:hover:text-selected-default-foreground-hover',
              'aria-pressed:active:text-selected-default-foreground-active',
              'aria-pressed:[&_svg]:text-selected-default-icon',
              'aria-pressed:hover:[&_svg]:text-selected-default-icon-hover',
              'aria-pressed:active:[&_svg]:text-selected-default-icon-active',
              'aria-pressed:border-selected-default-stroke',
              'aria-pressed:hover:border-selected-default-stroke-hover',
              'aria-pressed:active:border-selected-default-stroke-active',
              'aria-pressed:data-[state=open]:border-selected-default-stroke-active',
              'aria-pressed:data-[state=open]:bg-selected-default-background-hover',
            ],
            className,
          )}
        >
          {slots.leadingIcon ? (
            <>
              <Button.LeadingIcon
                data-spellbook-name="filter-chip-select-trigger-leading-icon"
                {...(Array.isArray(slots.leadingIcon) ? slots.leadingIcon[0]?.props : slots.leadingIcon?.props)}
              />
              {Array.isArray(slots.leadingIcon) && slots.leadingIcon.length > 1 && (
                <span className="flex-0 inline-flex gap-0">{slots.leadingIcon.slice(1)}</span>
              )}
            </>
          ) : null}
          <span className="inline-flex gap-1.5 items-center overflow-hidden min-h-6">
            {displayText}
            {overflowCount > 0 && (
              <NotificationBadge variant="primary" className="animate-in transition-all" size={notificationBadgeSize}>
                +{overflowCount}
              </NotificationBadge>
            )}
          </span>
          <Button.ToggleIcon />
        </Button>
      </DropdownMenuPrimitive.Trigger>
    );
  },
});

export const FilterChipSelectTriggerLeadingIconSlot = Slot<IconProps>({
  displayName: 'FilterChipSelect.TriggerLeadingIcon',
  renderDefault: ({ name = 'square-dashed', color = 'inherit', size: providedSize, ...props }) => {
    const size = getControlIconSize(providedSize);
    return <Icon data-spellbook-name="filter-chip-select-trigger-leading-icon" name={name} color={color} size={size} {...props} />;
  },
});

export const FilterChipSelectTooltipSlot = Slot<
  Omit<TooltipProps, 'asToggleTip'> & {
    'data-testid'?: string;
    value: FilterChipSelectProps['value'];
    defaultValue?: FilterChipSelectProps['defaultValue'];
    open?: FilterChipSelectProps['open'];
    size?: FilterChipSelectProps['size'];
    multiple?: FilterChipSelectProps['multiple'];
    disabled?: FilterChipSelectProps['disabled'];
  }
>({
  displayName: 'FilterChipSelect.Tooltip',
  spellbookName: false,
  preventOverride: true,
  renderDefault: ({
    className,
    children,
    side = 'top',
    align = 'center',
    'data-testid': dataTestId,
    defaultValue,
    value,
    open,
    size,
    multiple,
    disabled,
    ...props
  }) => {
    const { slots } = useSlots(children, {
      tooltipContent: { Component: FilterChipSelectTooltipContentSlot },
      trigger: {
        Component: FilterChipSelectTriggerSlot,
        props: { 'data-testid': dataTestId, defaultValue, value, open, size, multiple, disabled },
      },
    });

    return (
      <Tooltip className={cn('group', className)} side={side} align={align} {...props}>
        <Tooltip.Trigger>{slots.trigger}</Tooltip.Trigger>
        {slots.tooltipContent && <Tooltip.Content>{slots.tooltipContent}</Tooltip.Content>}
      </Tooltip>
    );
  },
});

export const FilterChipSelectTooltipContentSlot = Slot<TextProps>({
  displayName: 'FilterChipSelect.TooltipContent',
  preventOverride: true,
  renderDefault: ({ ...props }) => (
    <Text data-spellbook-name="filter-chip-select-tooltip-content" variant="caption-1" element="span" {...props} />
  ),
});

export const FilterChipSelectRightPanelSlot = Slot<React.ComponentProps<'div'>>({
  displayName: 'FilterChipSelect.RightPanel',
  renderDefault: ({ children, className, ...props }) => (
    <div
      data-spellbook-name="filter-chip-select-right-panel"
      aria-hidden="true"
      role="presentation"
      className={cn('flex-shrink-0', className)}
      {...props}
    >
      {children}
    </div>
  ),
});

export type FilterChipSelectContentProps = React.ComponentProps<typeof DropdownMenuPrimitive.Content>;

export const FilterChipSelectContentSlot = Slot<FilterChipSelectContentProps>({
  displayName: 'FilterChipSelect.Content',
  isRenderedByDefault: true,
  preventOverride: true,
  renderDefault: ({ children, className, align = 'start', onCloseAutoFocus, ...props }) => {
    const ref = useRef<HTMLDivElement>(null);
    const { multiple } = useFilterChipSelect();
    const { slots: contentSlots, rest: menuChildren } = useSlots(children, {
      rightPanel: { Component: FilterChipSelectRightPanelSlot },
    });

    const handleOnCloseAutoFocus = (event: Event) => {
      onCloseAutoFocus?.(event);
    };

    if (!children) {
      return null;
    }

    return (
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          data-spellbook-name="filter-chip-select-content"
          className={cn(
            ...popperContentSideOffsetClassNames,
            'bg-surface-overlay-0',
            ...popperContentMotionClassNames,
            'relative',
            'z-portal',
            'min-w-(--radix-dropdown-menu-trigger-width)',
            'max-w-(--radix-dropdown-menu-content-available-width)',
            'max-h-(--radix-dropdown-menu-content-available-height)',
            'origin-(--radix-dropdown-menu-content-transform-origin)',
            'overflow-x-hidden',
            'overflow-y-auto',
            'rounded-card-md',
            'shadow-popover',
            'border',
            'border-border-default',
            'group',
            'focus-visible:outline-none',
            className,
          )}
          align={align}
          onCloseAutoFocus={handleOnCloseAutoFocus}
          {...props}
        >
          <div className={cn('flex', !contentSlots.rightPanel && 'w-full')}>
            <div
              ref={ref}
              role={!multiple ? 'group' : undefined}
              className={cn(
                'w-full max-h-[min(18rem,var(--radix-dropdown-menu-content-available-height))]',
                contentSlots.rightPanel && 'flex-1 overflow-y-auto',
              )}
            >
              {menuChildren}
            </div>
            {contentSlots.rightPanel}
          </div>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    );
  },
});

export const FilterChipSelectHeadingSlot = Slot<React.ComponentProps<typeof DropdownMenuPrimitive.Label>>({
  displayName: 'FilterChipSelect.Heading',
  renderDefault: ({ children, className, ...props }) => {
    return (
      <DropdownMenuPrimitive.Label data-spellbook-name="filter-chip-select-heading" {...props} asChild>
        <Text variant="caption-1" color="default-soft" className="px-4 pt-4 flex">
          {children}
        </Text>
      </DropdownMenuPrimitive.Label>
    );
  },
});

export const FilterChipSelectGroupSlot = Slot<React.ComponentProps<typeof DropdownMenuPrimitive.Group>>({
  displayName: 'FilterChipSelect.Group',
  renderDefault: ({ children, className, ...props }) => {
    const { multiple } = useFilterChipSelect();

    if (!multiple) {
      console.warn('FilterChipSelect.Group is not supported when multiple is "false"');
      return <>{children}</>;
    }

    return (
      <DropdownMenuPrimitive.Group
        data-spellbook-name="filter-chip-select-group"
        className={cn('border-t border-t-border-default first-of-type:border-0', className)}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Group>
    );
  },
});

export const FilterChipSelectItemLeadingIconSlot = Slot<IconProps>({
  displayName: 'FilterChipSelect.ItemLeadingIcon',
  renderDefault: ({ name = 'square-dashed', color = 'default', size = 'sm', ...props }) => (
    <Icon data-spellbook-name="filter-chip-select-item-leading-icon" name={name} color={color} size={size} {...props} />
  ),
});

export const FilterChipSelectItemTrailingIconSlot = Slot<IconProps>({
  displayName: 'FilterChipSelect.ItemTrailingIcon',
  renderDefault: ({ name = 'square-dashed', color = 'default', size = 'sm', ...props }) => (
    <Icon data-spellbook-name="filter-chip-select-item-trailing-icon" name={name} color={color} size={size} {...props} />
  ),
});

export const FilterChipSelectItemLabelSlot = Slot<TextProps & { label?: React.ReactNode }>({
  displayName: 'FilterChipSelect.ItemLabel',
  isRenderedByDefault: true,
  renderDefault: ({ children, variant = 'body-2', element = 'span', label, color = 'inherit', className, ...props }) => {
    return (
      <Text
        data-spellbook-name="filter-chip-select-item-label"
        variant={variant}
        element={element}
        color={color}
        className={cn('max-w-full shrink-0', className)}
        {...props}
      >
        {label}
        {children}
      </Text>
    );
  },
});

export const FilterChipSelectItemDescriptionSlot = Slot<TextProps & { label?: React.ReactNode }>({
  displayName: 'FilterChipSelect.ItemDescription',
  renderDefault: ({ children, className, variant = 'caption-1', element = 'span', label, color = 'default-soft', ...props }) => {
    return (
      <Text
        data-spellbook-name="filter-chip-select-item-description"
        variant={variant}
        element={element}
        color={color}
        className={cn('max-w-full shrink-0', className)}
        {...props}
      >
        {label}
        {children}
      </Text>
    );
  },
});

export type FilterChipSelectItemProps = React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem> & {
  label?: React.ReactNode;
  description?: React.ReactNode;
  value: string;
};

const itemVariants = cva([
  'flex',
  'cursor-pointer',
  'items-center',
  'justify-between',
  'outline-none',
  'pr-4',
  'pl-3',
  'rtl:pr-3',
  'rtl:pl-4',
  'py-2',
  'gap-2',
  /**
   * @TODO [SPE-16] Centralize "Selected" Color Tokens into Shared Style Utilities
   * https://linear.app/magicschool/issue/SPE-16/centralize-selected-color-tokens-into-shared-style-utilities
   */
  'data-[state=checked]:bg-selected-default-background',
  'data-[state=checked]:hover:bg-selected-default-background-hover',
  'data-[state=checked]:active:bg-selected-default-background-active',
  'data-[state=checked]:focus-visible:bg-selected-default-background-hover',
  'data-[state=checked]:text-selected-default-foreground',
  'data-[state=checked]:hover:text-selected-default-foreground-hover',
  'data-[state=checked]:focus-visible:text-selected-default-foreground-hover',
  'data-[state=checked]:active:text-selected-default-foreground-active',
  'data-[highlighted]:bg-surface-overlay-1',
  'data-[highlighted]:data-[state=checked]:bg-selected-default-background-hover',
  'hover:bg-surface-overlay-1',
  'focus:bg-surface-overlay-1',
  'text-default-text-strong',
  'data-[state=checked]:text-primary-text',
  'group',
  'aria-disabled:hover:bg-surface-overlay-0',
  'aria-disabled:text-disabled-foreground',
  'aria-disabled:cursor-not-allowed',
]);

const itemCheckboxVariants = cva('flex-0 inline-flex h-4 w-4 m-1 items-center justify-center min-w-4 min-h-4 rounded-sm border-[1.5px]', {
  variants: {
    isChecked: {
      true: [
        'bg-primary-solid-background-hover',
        'border-transparent',
        'text-primary-solid-foreground-text',
        'group-hover:bg-primary-solid-background-hover',
        'group-active:bg-primary-solid-background-active',
      ],
      false: 'bg-surface-background-0 border-checkbox group-hover:border-checkbox-hover group-hover:bg-surface-background-2',
    },
  },
});

export const FilterChipSelectItemSlot = Slot<FilterChipSelectItemProps>({
  displayName: 'FilterChipSelect.Item',
  renderDefault: ({
    label,
    description,
    className,
    disabled,
    children,
    onCheckedChange: providedOnCheckedChange,
    value: providedValue,
    ...props
  }) => {
    const { value, onCheckedChange, multiple, setHighlightedValue } = useFilterChipSelect();

    const handleOnCheckedChange = (checked: boolean) => {
      if (providedOnCheckedChange) {
        providedOnCheckedChange(checked);
      }

      if (onCheckedChange) {
        if (!multiple) {
          onCheckedChange(checked ? (providedValue as string) : undefined);
        } else {
          const currentValues = Array.isArray(value) ? value : [];
          const newValue = checked
            ? [...currentValues, providedValue as string]
            : currentValues.filter((item) => item !== (providedValue as string));

          onCheckedChange(newValue);
        }
      }
    };

    const { slots, rest } = useSlots(children, {
      leadingIcon: { Component: FilterChipSelectItemLeadingIconSlot, props },
      trailingIcon: { Component: FilterChipSelectItemTrailingIconSlot, props },
      label: {
        Component: FilterChipSelectItemLabelSlot,
        props: {
          label,
          ...(disabled && { color: 'disabled' }),
        },
      },
      description: {
        Component: FilterChipSelectItemDescriptionSlot,
        props: {
          ...props,
          label: description,
          ...(disabled && { color: 'disabled' }),
        },
      },
    });

    /**
     * @TODO [SPE-37] Utility for post-slot prop updates
     * https://linear.app/magicschool/issue/SPE-37/utility-for-post-slot-prop-updates
     */
    if (rest) {
      const labelSlot = slots.label;
      let labelSlotProps;
      if (Array.isArray(labelSlot) && labelSlot[0]) {
        labelSlotProps = {
          ...(labelSlot[0].props ?? {}),
          label: (
            <>
              {labelSlot[0].props.label}
              {rest}
            </>
          ),
        };
        slots.label = [
          {
            ...labelSlot[0],
            type: labelSlot[0].type,
            props: labelSlotProps,
          },
          ...labelSlot.slice(1),
        ];
      } else if (labelSlot && !Array.isArray(labelSlot)) {
        labelSlotProps = {
          ...(labelSlot.props ?? {}),
          label: (
            <>
              {labelSlot.props.label}
              {rest}
            </>
          ),
        };
        slots.label = {
          ...labelSlot,
          props: labelSlotProps,
        };
      }
    }

    if (!multiple) {
      const isSingleChecked = !Array.isArray(value) && value === providedValue;
      return (
        <DropdownMenuPrimitive.CheckboxItem
          data-spellbook-name="filter-chip-select-item"
          disabled={disabled}
          className={cn(itemVariants({}), className)}
          aria-disabled={disabled}
          {...props}
          role="menuitemradio"
          checked={isSingleChecked}
          onCheckedChange={handleOnCheckedChange}
          onMouseEnter={() => setHighlightedValue(providedValue)}
          onFocus={() => setHighlightedValue(providedValue)}
        >
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2 overflow-hidden">
              {slots.leadingIcon}
              <span className="flex grow-1 me-4 flex-col">
                {slots.label}
                {slots.description}
              </span>
            </span>
          </span>
          <DropdownMenuPrimitive.ItemIndicator
            className={cn(
              'text-sm',
              'group-data-[state=checked]:text-selected-default-icon',
              'group-data-[state=checked]:group-hover:text-selected-default-icon-hover',
              'group-data-[state=checked]:group-focus-visible:text-selected-default-icon-hover',
              'group-data-[state=checked]:group-active:text-selected-default-icon-active',
            )}
          >
            <Icon name="check" color="inherit" size="sm" />
          </DropdownMenuPrimitive.ItemIndicator>
          {slots.trailingIcon && <span className="flex-0">{slots.trailingIcon}</span>}
        </DropdownMenuPrimitive.CheckboxItem>
      );
    } else {
      const isChecked = Array.isArray(value) && value.includes(providedValue as string);

      return (
        <DropdownMenuPrimitive.CheckboxItem
          data-spellbook-name="filter-chip-select-item"
          disabled={disabled}
          className={cn(itemVariants({}), className)}
          aria-disabled={disabled}
          {...props}
          onCheckedChange={handleOnCheckedChange}
          checked={isChecked}
          onMouseEnter={() => setHighlightedValue(providedValue)}
          onFocus={() => setHighlightedValue(providedValue)}
        >
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2 overflow-hidden">
              <span className={cn(itemCheckboxVariants({ isChecked }))}>
                {isChecked && <Icon name="check" color="inherit" size="xs" />}
              </span>

              {slots.leadingIcon}
              <span className="flex grow-1 me-4 flex-col">
                {slots.label}
                {slots.description}
              </span>
            </span>
          </span>
          {slots.trailingIcon && <span className="flex-0">{slots.trailingIcon}</span>}
        </DropdownMenuPrimitive.CheckboxItem>
      );
    }
  },
});
